"use client";

import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useRef, useTransition } from "react";
import { useRouter } from "next/navigation";
import { GiftSectionFormValue } from "@/checkout/Sections/SubscriptionSection/types";
import { CheckoutForm } from "@/checkout/types";
import { getSelectedGiftSkusFromForm } from "@/checkout/subscription/gift-selection";
import { useTrackEvent } from "@/tracking/TrackingContext";
import { GiftSectionPicker } from "./GiftSectionPicker";
import { GiftSelectionBar } from "./GiftSelectionBar";

type GiftSelectorFormProps = {
  subscriptionSku: string;
  giftSections: GiftSectionFormValue[];
  backHref: string;
};

export type GiftSelectionFormValues = Pick<
  CheckoutForm,
  "gifts" | "giftSections"
>;

export const GiftSelectorForm = ({
  subscriptionSku,
  giftSections,
  backHref,
}: GiftSelectorFormProps) => {
  const router = useRouter();
  const trackEvent = useTrackEvent();
  const formRef = useRef<HTMLFormElement>(null);
  const [isPending, startTransition] = useTransition();

  const form = useForm<GiftSelectionFormValues>({
    mode: "onSubmit",
    defaultValues: {
      gifts: [],
      giftSections,
    },
  });

  const onSubmit: SubmitHandler<GiftSelectionFormValues> = (data) => {
    if (isPending) return;
    const selectedGiftSkus = getSelectedGiftSkusFromForm(data);

    const searchParams = new URLSearchParams();

    selectedGiftSkus.forEach((giftSku) => {
      searchParams.append("gift", giftSku);
    });

    const queryString = searchParams.toString();
    trackEvent({
      eventName: "subscription_gift_selection_completed",
      sku: subscriptionSku,
      selected_gift_count: selectedGiftSkus.length,
    });
    startTransition(() => {
      router.push(
        queryString
          ? `/innmelding/${subscriptionSku}?${queryString}`
          : `/innmelding/${subscriptionSku}`,
      );
    });
  };

  const onInvalid = () => {
    setTimeout(() => {
      formRef.current
        ?.querySelector(".text-status-error-500")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
  };

  return (
    <FormProvider {...form}>
      <form
        ref={formRef}
        className="mt-10 space-y-12"
        onSubmit={form.handleSubmit(onSubmit, onInvalid)}
        noValidate
      >
        {giftSections.map((section, index) => (
          <GiftSectionPicker
            key={index}
            sectionIndex={index}
            count={section.count}
            options={section.options}
          />
        ))}
        <GiftSelectionBar backHref={backHref} isPending={isPending} />
      </form>
    </FormProvider>
  );
};
